import React, { useEffect, useMemo, useState } from 'react';
import { LuTriangleAlert, LuClock, LuCalendarDays } from 'react-icons/lu';
import ChildSelectionModal from '../../components/ChildSelectionModal';
import ChildSwitcherButton from '../../components/ChildSwitcherButton';
import LessonIcon, { levelFromGrade } from '../../components/LessonIcon';
import { useChild } from '../../contexts/ChildContext';
import { parentApi } from '../../services/api';
import AwaitingClassNotice from '../../components/AwaitingClassNotice';
import { awaitingChildAssignmentsText } from '../../utils/classStatus';
import SegmentedSwitch from '../../components/SegmentedSwitch';
import FilterPills from '../../components/FilterPills';

/**
 * Parent → "Öğrenci Ödevleri". Read-only mirror of the student "Ödevlerim":
 * identical filters (Ders / Durum / Teslim Tarihi) and cards, plus a read-only
 * detail view — Test Ödevi shows the result summary (no Kitabı Aç, no solving),
 * Diğer Ödev shows the teacher's file, the student's uploaded file + note, and
 * the grade (no upload). Data comes from /parent/children/:id/assignments which
 * delegates to the same student builder.
 */

type IconCmp = React.FC<{ size?: number; className?: string; color?: string }>;
const AlertIcon = LuTriangleAlert as IconCmp;
const ClockLuIcon = LuClock as IconCmp;
const CalendarIcon = LuCalendarDays as IconCmp;

interface Assignment {
  id: string;
  title: string;
  subject: string;
  teacherName?: string | null;
  type: 'test' | 'odev';
  status: 'pending' | 'completed';
  openDate: string;
  closeDate: string;
  closeDateRaw?: string | null;
  dueDateRaw?: string | null;
  questionCount: number;
  correctCount: number;
  wrongCount: number;
  blankCount: number;
  totalPoints: number;
  earnedPoints: number;
  timeSpent: string;
  timeSpentSeconds: number | null;
  completedAt: string | null;
  teacherNote?: string;
  attachmentUrl?: string | null;
  submissionFileUrl?: string | null;
  studentNotes?: string | null;
  graded?: boolean;
  gradeScore?: number | null;
  teacherFeedback?: string | null;
  late?: boolean;
  otherFields?: { questionCount?: string; subject?: string; unit?: string; outcome?: string } | null;
  learningOutcomes: { type: 'meb' | 'book'; id: number; code: string | null; label: string }[];
}

type Modal =
  | null
  | { type: 'sonuclar'; assignment: Assignment }
  | { type: 'diger-odev'; assignment: Assignment }
  | { type: 'test-pending'; assignment: Assignment };

const CloseButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button onClick={onClick} className="w-[48px] h-[48px] rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors absolute top-6 right-6">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" />
    </svg>
  </button>
);

const ClockIcon: React.FC<{ size?: number; color?: string }> = ({ size = 18, color = '#1e1e1e' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);

function fmtDeadline(iso: string | null | undefined): string {
  if (!iso) return 'Süresiz';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Süresiz';
  const tr = new Date(d.getTime() + 3 * 3600 * 1000);
  const dd = String(tr.getUTCDate()).padStart(2, '0');
  const mo = String(tr.getUTCMonth() + 1).padStart(2, '0');
  const yy = tr.getUTCFullYear();
  const hh = tr.getUTCHours();
  const mn = tr.getUTCMinutes();
  return `${dd}.${mo}.${yy} ${String(hh).padStart(2, '0')}:${String(mn).padStart(2, '0')}`;
}

type DeadlineTier = 'overdue' | 'urgent' | 'soon' | 'normal' | 'done' | 'none';
interface DeadlineInfo { tier: DeadlineTier; label: string; icon: 'alert' | 'clock' | 'calendar'; fg: string; bg: string; accent: string }
const DAY_MS = 86400000;
const DL_GRAY = { fg: '#6b7280', bg: '#f3f4f6' };
const DL_RED = { fg: '#dc2626', bg: '#fee2e2' };
const DL_AMBER = { fg: '#b45309', bg: '#fef3c7' };
function getDeadline(closeDateRaw: string | null | undefined, closeDate: string, completed: boolean): DeadlineInfo {
  if (!closeDateRaw) return { tier: 'none', label: 'Süresiz', icon: 'calendar', ...DL_GRAY, accent: 'transparent' };
  const due = new Date(closeDateRaw).getTime();
  if (Number.isNaN(due)) return { tier: 'none', label: closeDate || 'Süresiz', icon: 'calendar', ...DL_GRAY, accent: 'transparent' };
  if (completed) return { tier: 'done', label: closeDate, icon: 'calendar', ...DL_GRAY, accent: 'transparent' };
  const diff = due - Date.now();
  if (diff < 0) return { tier: 'overdue', label: `Yapılmadı · ${closeDate}`, icon: 'alert', ...DL_RED, accent: '#dc2626' };
  const hours = diff / 3600000;
  if (hours <= 24) return { tier: 'urgent', label: `${hours < 1 ? 'Son dakikalar' : `${Math.max(1, Math.floor(hours))} saat kaldı`} · ${closeDate}`, icon: 'alert', ...DL_RED, accent: '#dc2626' };
  const days = Math.ceil(diff / DAY_MS);
  if (days <= 3) return { tier: 'soon', label: `${days} gün kaldı · ${closeDate}`, icon: 'clock', ...DL_AMBER, accent: '#f59e0b' };
  return { tier: 'normal', label: closeDate, icon: 'calendar', ...DL_GRAY, accent: 'transparent' };
}

const DeadlineBadge: React.FC<{ info: DeadlineInfo }> = ({ info }) => {
  const Icon = info.icon === 'alert' ? AlertIcon : info.icon === 'clock' ? ClockLuIcon : CalendarIcon;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 self-start" style={{ background: info.bg, color: info.fg }}>
      <Icon size={13} color={info.fg} />
      <span style={{ fontFamily: 'Inter', fontSize: 12.5, fontWeight: 700 }}>{info.label}</span>
    </span>
  );
};

const OutcomeChips: React.FC<{ outcomes: Assignment['learningOutcomes'] }> = ({ outcomes }) => {
  if (!outcomes || outcomes.length === 0) return null;
  return (
    <div className="bg-white rounded-[12px] p-4 border border-gray-200 mb-4">
      <p className="text-[13px] font-bold text-[#3973bf] mb-2">MEB Kazanımları / Kitap Konuları</p>
      <div className="flex flex-wrap gap-2">
        {outcomes.map((o, i) => (
          <span key={i} className="rounded-full px-3 py-1" style={{ background: o.type === 'meb' ? '#eef3fd' : '#fdeef5', color: o.type === 'meb' ? '#3973bf' : '#e5599b', fontFamily: 'Inter', fontSize: 12, fontWeight: 500 }}>
            {o.type === 'meb' ? 'MEB · ' : 'Konu · '}{o.code ? `${o.code} · ` : ''}{o.label}
          </span>
        ))}
      </div>
    </div>
  );
};

function mapAssignment(s: any): Assignment {
  const ass = s.assignment || {};
  const grade = s.assignment_grades;
  const attempt = s.attempt_stats ?? null;
  const isCompleted = ['submitted', 'graded', 'returned'].includes(s.status);
  const teacher = ass.users ?? ass.teacher ?? ass.created_by_user ?? null;
  const teacherName = teacher ? `${teacher.first_name ?? ''} ${teacher.last_name ?? ''}`.trim() || null : null;
  const questionCount = attempt?.score_total ?? 0;
  const correctCount = attempt?.score_correct ?? 0;
  const wrongCount = attempt?.score_wrong ?? 0;
  const blankCount = attempt?.score_blank ?? 0;
  const earnedPoints = grade?.score ?? (attempt ? Math.round((correctCount / (questionCount || 1)) * 100) : 0);
  const secs = s.timeSpentSeconds ?? attempt?.time_spent_seconds ?? null;
  let timeSpent = '-';
  if (secs != null && secs > 0) {
    const m = Math.floor(secs / 60);
    const sec = secs % 60;
    timeSpent = m > 0 ? `${m} Dk ${sec > 0 ? `${sec} Sn` : ''}`.trim() : `${sec} Sn`;
  }
  return {
    id: String(s.id),
    title: ass.title || 'Ödev',
    subject: ass.subject?.name || '',
    teacherName,
    type: ass.assignment_type === 'other' ? 'odev' : 'test',
    status: isCompleted ? 'completed' : 'pending',
    openDate: ass.created_at ? new Date(ass.createdAt || ass.created_at).toLocaleDateString('tr-TR') : 'Süresiz',
    closeDate: fmtDeadline(ass.due_date),
    closeDateRaw: ass.due_date ?? ass.created_at ?? null,
    dueDateRaw: ass.due_date ?? null,
    questionCount, correctCount, wrongCount, blankCount,
    totalPoints: 100, earnedPoints,
    timeSpent, timeSpentSeconds: secs,
    completedAt: s.submittedAt ?? attempt?.submitted_at ?? null,
    teacherNote: ass.description,
    attachmentUrl: ass.attachment_url ?? null,
    submissionFileUrl: s.submission_file_url ?? null,
    studentNotes: s.student_notes ?? null,
    graded: s.status === 'graded',
    gradeScore: grade?.score != null ? Number(grade.score) : null,
    teacherFeedback: grade?.feedback ?? null,
    late: !!s.late,
    otherFields: ass.other_fields ?? null,
    learningOutcomes: ass.learning_outcomes ?? [],
  };
}

const ParentStudentAssignments: React.FC = () => {
  const { selectedChild, children: kids, loading: childLoading } = useChild();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<Modal>(null);
  const [subjectFilter, setSubjectFilter] = useState('');
  const [tab, setTab] = useState<'odevler' | 'notlandirma'>('odevler');
  const [timeFilter, setTimeFilter] = useState<'all' | 'today' | 'week' | 'month' | 'undated'>('all');

  const studentLevel = levelFromGrade(selectedChild?.gradeLevel?.number ?? null);

  useEffect(() => {
    if (!selectedChild) return;
    setLoading(true);
    parentApi.childAssignments(selectedChild.id)
      .then((res) => setAssignments((res.data ?? []).map(mapAssignment)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedChild]);

  const filterBySubject = (a: Assignment) => !subjectFilter || (a.subject ?? '').toLowerCase() === subjectFilter.toLowerCase();
  const pendingCount = assignments.filter((a) => a.status === 'pending' && filterBySubject(a)).length;
  const totalPending = assignments.filter((a) => a.status === 'pending').length;

  const subjectStats = useMemo(() => {
    const map = new Map<string, { total: number; pending: number }>();
    for (const a of assignments) {
      if (!a.subject) continue;
      const cur = map.get(a.subject) ?? { total: 0, pending: 0 };
      cur.total += 1;
      if (a.status === 'pending') cur.pending += 1;
      map.set(a.subject, cur);
    }
    return Array.from(map.entries()).map(([subject, stats]) => ({ subject, ...stats })).sort((a, b) => b.pending - a.pending || a.subject.localeCompare(b.subject, 'tr'));
  }, [assignments]);

  const statusOf = (a: Assignment): 'notdone' | 'submitted' | 'complete' => a.status === 'pending' ? 'notdone' : a.graded ? 'complete' : 'submitted';

  const withinWindow = (iso: string | null | undefined): boolean => {
    if (timeFilter === 'all') return true;
    if (timeFilter === 'undated') return !iso;
    if (!iso) return false;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return false;
    const now = new Date();
    let start: Date, end: Date;
    if (timeFilter === 'today') {
      start = new Date(now); start.setHours(0, 0, 0, 0);
      end = new Date(now); end.setHours(23, 59, 59, 999);
    } else if (timeFilter === 'week') {
      start = new Date(now); const dow = (start.getDay() + 6) % 7; start.setDate(start.getDate() - dow); start.setHours(0, 0, 0, 0);
      end = new Date(start); end.setDate(start.getDate() + 6); end.setHours(23, 59, 59, 999);
    } else {
      start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    }
    return t >= start.getTime() && t <= end.getTime();
  };

  const handleCardClick = (a: Assignment) => {
    if (a.type === 'odev') setModal({ type: 'diger-odev', assignment: a });
    else if (a.status === 'completed') setModal({ type: 'sonuclar', assignment: a });
    else setModal({ type: 'test-pending', assignment: a });
  };

  const displayed = assignments
    .filter(filterBySubject)
    // Ödevler = not-done only; Notlandırma = submitted (awaiting) + graded.
    .filter((a) => (tab === 'odevler' ? statusOf(a) === 'notdone' : statusOf(a) !== 'notdone'))
    // Teslim Tarihi filter only applies on the Ödevler tab.
    .filter((a) => (tab === 'notlandirma' ? true : withinWindow(a.dueDateRaw)))
    .sort((a, b) => {
      const ta = a.completedAt ? new Date(a.completedAt).getTime() : (a.closeDateRaw ? new Date(a.closeDateRaw).getTime() : 0);
      const tb = b.completedAt ? new Date(b.completedAt).getTime() : (b.closeDateRaw ? new Date(b.closeDateRaw).getTime() : 0);
      return tb - ta;
    });

  if (childLoading) {
    return <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (kids.length === 0) {
    return <p className="text-center text-gray-400 py-16">Henüz öğrenci eklenmedi.</p>;
  }
  if (!selectedChild) {
    return <ChildSelectionModal context="Ödevler" />;
  }

  const renderTeacherFile = (url: string) => {
    const ext = (url.split('?')[0].split('.').pop() || '').toLowerCase();
    const isPdf = ext === 'pdf';
    const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
    if (isPdf) return <iframe src={url} title="Ödev Dosyası" className="w-full h-[440px] rounded-[12px] border border-gray-200 bg-white" />;
    if (isImage) return <img src={url} alt="Ödev Dosyası" className="w-full rounded-[12px] border border-gray-200 bg-white" />;
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#3973bf] hover:underline">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3973bf" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
        Dosyayı Görüntüle
      </a>
    );
  };

  return (
    <div>
      <div className="flex items-center gap-x-4 gap-y-2 mb-5 flex-wrap">
        <h1 style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Ödevler</h1>
        <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 500, color: '#3973bf' }}>- {selectedChild.name} {selectedChild.surname}</span>
        <ChildSwitcherButton />
        {/* Ödevler (yapılacak) / Notlandırma (gönderilen + notlandırılan) */}
        <SegmentedSwitch
          ariaLabel="Ödev görünümü"
          value={tab}
          onChange={setTab}
          options={[{ value: 'odevler', label: 'Ödevler' }, { value: 'notlandirma', label: 'Notlandırma' }]}
        />
        <span className="text-[14px] lg:text-[16px] text-gray-500">
          <span className="font-semibold text-[#3973bf]">{pendingCount} adet tamamlanmamış</span> ödev bulunmaktadır
        </span>
      </div>

      {/* Ders filtresi: yalnızca ödevi olan dersler; sayı etiketin içinde. */}
      <div className="space-y-3 mb-5">
        {subjectStats.length > 0 && (
          <FilterPills
            label="Ders"
            tone="blue"
            value={subjectStats.find((x) => x.subject.toLowerCase() === subjectFilter.toLowerCase())?.subject ?? ''}
            onChange={(v) => setSubjectFilter(v === subjectFilter ? '' : v)}
            options={[
              { value: '', label: `Tümü (${totalPending})` },
              ...subjectStats.map(({ subject, pending }) => ({ value: subject, label: `${subject} (${pending})` })),
            ]}
          />
        )}

        {/* Teslim tarihi: yalnızca Ödevler sekmesinde. */}
        {tab === 'odevler' && (
          <FilterPills
            label="Teslim tarihi"
            tone="pink"
            value={timeFilter}
            onChange={setTimeFilter}
            options={[{ value: 'all', label: 'Tüm Zamanlar' }, { value: 'today', label: 'Bugün' }, { value: 'week', label: 'Bu Hafta' }, { value: 'month', label: 'Bu Ay' }, { value: 'undated', label: 'Süresiz' }]}
          />
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
      ) : displayed.length === 0 ? (
        tab === 'odevler' && selectedChild.classStatus === 'awaiting' ? (
          // Kurumlu ama sınıfsız: class homework cannot exist yet — say so.
          <AwaitingClassNotice text={awaitingChildAssignmentsText(selectedChild.name)} />
        ) : (
          <p className="text-center text-gray-400 py-16">
            {tab === 'odevler' ? 'Yapılacak ödev bulunmuyor.' : 'Notlandırma bekleyen veya notlandırılmış ödev bulunmuyor.'}
          </p>
        )
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 items-stretch">
          {displayed.map((a) => {
            const completed = a.status === 'completed';
            const dl = getDeadline(a.closeDateRaw, a.closeDate, completed);
            const typeLabel = a.type === 'test' ? 'Test Ödevi' : 'Diğer Ödev';
            const urgent = dl.tier === 'overdue' || dl.tier === 'urgent';
            const circleBg = completed ? '#179e3b' : urgent ? '#dc2626' : dl.tier === 'soon' ? '#f59e0b' : '#ffda09';
            const accentShadow = dl.accent !== 'transparent' ? `, inset 4px 0 0 ${dl.accent}` : '';
            let statusText: string, statusColor: string;
            if (completed) {
              if (a.type === 'test' || a.graded) { statusText = `Notlandırıldı · ${a.type === 'test' ? a.earnedPoints : (a.gradeScore ?? '-')}/100`; statusColor = '#179e3b'; }
              else { statusText = 'Notlandırma Bekliyor'; statusColor = '#a97e00'; }
            } else { statusText = 'Henüz başlanmadı'; statusColor = '#9ca3af'; }
            return (
              <div key={a.id} onClick={() => handleCardClick(a)}
                className="bg-[#fafafa] rounded-[12px] p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow min-h-[132px] h-full"
                style={{ boxShadow: `0px 1px 4px rgba(0,0,0,0.12)${accentShadow}` }}>
                <div className="w-[75px] h-[75px] rounded-[8px] flex items-center justify-center flex-shrink-0" style={{ background: '#3973bf', boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                  <LessonIcon subject={a.subject} level={studentLevel} size={56}
                    fallback={<img src="/a-odevicon.png" alt="" className="w-[56px] h-[45px] object-contain" style={{ filter: 'drop-shadow(0px 4px 4px rgba(0,0,0,0.25))' }} />} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 700, color: '#2b2b2b' }} className="truncate pr-1">{a.title}</p>
                    <div className="w-[23px] h-[23px] rounded-full flex items-center justify-center flex-shrink-0" style={{ background: circleBg }}>
                      {completed
                        ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        : urgent ? <AlertIcon size={13} color="#fff" /> : <ClockIcon size={14} color={dl.tier === 'soon' ? '#fff' : '#1e1e1e'} />}
                    </div>
                  </div>
                  <p style={{ fontFamily: 'Inter', fontSize: 14, color: '#777', marginBottom: 4 }} className="truncate">{a.subject ? `${typeLabel} · ${a.subject}` : typeLabel}</p>
                  <p style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 600, color: statusColor, marginBottom: 8 }}>{statusText}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <DeadlineBadge info={dl} />
                    {a.late && (
                      <span className="inline-flex items-center rounded-full px-2.5 py-1 self-start" style={{ background: '#fee2e2', color: '#dc2626', fontFamily: 'Inter', fontSize: 12.5, fontWeight: 700 }}>Geç Teslim</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sonuçlar Modal (Test Ödevi — read-only, no Kitabı Aç) */}
      {modal?.type === 'sonuclar' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setModal(null)} />
          <div className="relative bg-[#f7f7f7] rounded-[24px] w-full max-w-[1000px] p-8 lg:p-10" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
            <CloseButton onClick={() => setModal(null)} />
            <h2 style={{ fontFamily: 'Inter', fontSize: 32, fontWeight: 700, color: '#3973bf', marginBottom: 24 }}>Sonuçlar</h2>
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1 space-y-4">
                <div className="bg-[#fafafa] rounded-[20px] p-5" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                  <p style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 600, color: '#2b2b2b' }}>Ödev</p>
                  <p style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 500, color: '#2b2b2b', marginTop: 8 }}>{modal.assignment.title}</p>
                  {modal.assignment.teacherName && <p style={{ fontFamily: 'Inter', fontSize: 14, color: '#777', marginTop: 4 }}>Öğretmen: <strong>{modal.assignment.teacherName}</strong></p>}
                  {modal.assignment.completedAt && <p style={{ fontFamily: 'Inter', fontSize: 14, color: '#777', marginTop: 4 }}>Tamamlandı: {new Date(modal.assignment.completedAt).toLocaleDateString('tr-TR')}</p>}
                  {modal.assignment.late && <span className="inline-flex items-center rounded-full px-3 py-1 mt-2" style={{ background: '#fee2e2', color: '#dc2626', fontFamily: 'Inter', fontSize: 13, fontWeight: 700 }}>Geç Teslim</span>}
                </div>
                <div className="bg-[#fafafa] rounded-[20px] p-5 flex items-center gap-4" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                  <ClockIcon size={23} color="#2b2b2b" />
                  <div>
                    <p style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 500, color: '#2b2b2b' }}>Geçirilen Süre</p>
                    <p style={{ fontFamily: 'Inter', fontSize: 32, fontWeight: 600, color: '#2b2b2b', marginTop: 4 }}>{modal.assignment.timeSpent}</p>
                  </div>
                </div>
              </div>
              <div className="w-full lg:w-[353px] space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-[#fafafa] rounded-[20px] p-4 text-center" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                    <p style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 500, color: '#2b2b2b' }}>Soru</p>
                    <p style={{ fontFamily: 'Inter', fontSize: 28, fontWeight: 700, color: '#2b2b2b' }}>{modal.assignment.questionCount || '-'}</p>
                  </div>
                  <div className="bg-[#fafafa] rounded-[20px] p-4 text-center" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                    <p style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 500, color: '#3973bf' }}>Puan</p>
                    <p style={{ fontFamily: 'Inter', fontSize: 28, fontWeight: 700, color: '#3973bf' }}>{modal.assignment.earnedPoints}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-[#fafafa] rounded-[16px] p-3 text-center" style={{ border: '2px solid #59e56e' }}>
                    <p style={{ fontFamily: 'Inter', fontSize: 12, color: '#27a745' }}>Doğru</p>
                    <p style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 700, color: '#27a745' }}>{modal.assignment.correctCount}</p>
                  </div>
                  <div className="bg-[#fafafa] rounded-[16px] p-3 text-center" style={{ border: '2px solid #e53e3e' }}>
                    <p style={{ fontFamily: 'Inter', fontSize: 12, color: '#e53e3e' }}>Yanlış</p>
                    <p style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 700, color: '#e53e3e' }}>{modal.assignment.wrongCount}</p>
                  </div>
                  <div className="bg-[#fafafa] rounded-[16px] p-3 text-center" style={{ border: '2px solid #f5c518' }}>
                    <p style={{ fontFamily: 'Inter', fontSize: 12, color: '#a97e00' }}>Boş</p>
                    <p style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 700, color: '#a97e00' }}>{modal.assignment.blankCount}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6"><OutcomeChips outcomes={modal.assignment.learningOutcomes} /></div>
            <div className="flex justify-end mt-8">
              <button onClick={() => setModal(null)} className="bg-white rounded-[12px] px-8 py-4 text-[20px] font-bold text-[#2b2b2b] border-2 border-gray-200 hover:bg-gray-50 transition-colors">Geri Dön</button>
            </div>
          </div>
        </div>
      )}

      {/* Test Ödevi — pending (öğrenci henüz çözmedi) */}
      {modal?.type === 'test-pending' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setModal(null)} />
          <div className="relative bg-[#f7f7f7] rounded-[24px] w-full max-w-[560px] p-8" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
            <CloseButton onClick={() => setModal(null)} />
            <h2 style={{ fontFamily: 'Inter', fontSize: 26, fontWeight: 700, color: '#3973bf', marginBottom: 16 }}>Test Ödevi</h2>
            <div className="bg-[#fafafa] rounded-[16px] p-5 mb-4" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
              <p style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 600, color: '#2b2b2b' }}>{modal.assignment.title}</p>
              {modal.assignment.teacherName && <p style={{ fontFamily: 'Inter', fontSize: 14, color: '#777', marginTop: 6 }}>Öğretmen: <strong>{modal.assignment.teacherName}</strong></p>}
              <p style={{ fontFamily: 'Inter', fontSize: 14, color: '#777', marginTop: 4 }}>Kapanış: {modal.assignment.closeDate}</p>
            </div>
            <div className="mb-4"><OutcomeChips outcomes={modal.assignment.learningOutcomes} /></div>
            <p style={{ fontFamily: 'Inter', fontSize: 15, color: '#a97e00', fontWeight: 600 }}>Öğrenci bu testi henüz çözmedi.</p>
          </div>
        </div>
      )}

      {/* Diğer Ödev Modal — read-only */}
      {modal?.type === 'diger-odev' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setModal(null)} />
          <div className="relative bg-[#f7f7f7] rounded-[24px] w-full max-w-[1000px] max-h-[90vh] overflow-y-auto p-8 lg:p-10" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
            <CloseButton onClick={() => setModal(null)} />
            <h2 style={{ fontFamily: 'Inter', fontSize: 32, fontWeight: 700, marginBottom: 24 }}>
              <span style={{ color: '#2b2b2b' }}>Ödev: </span><span style={{ color: '#3973bf' }}>{modal.assignment.title}</span>
            </h2>
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1">
                <div className="mb-6 max-h-[300px] overflow-y-auto pr-2">
                  <p style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 400, color: '#2b2b2b', lineHeight: '1.6' }}>{modal.assignment.teacherNote || 'Öğretmen açıklaması bulunmamaktadır.'}</p>
                </div>
                {modal.assignment.otherFields && Object.keys(modal.assignment.otherFields).length > 0 && (
                  <div className="bg-white rounded-[12px] p-4 border border-gray-200">
                    <p className="text-[13px] font-bold text-[#3973bf] mb-2">Diğer Bilgiler</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
                      {modal.assignment.otherFields.questionCount && <p><span className="text-gray-500">Soru Sayısı: </span><strong>{modal.assignment.otherFields.questionCount}</strong></p>}
                      {modal.assignment.otherFields.subject && <p><span className="text-gray-500">Ders: </span><strong>{modal.assignment.otherFields.subject}</strong></p>}
                      {modal.assignment.otherFields.unit && <p><span className="text-gray-500">Ünite: </span><strong>{modal.assignment.otherFields.unit}</strong></p>}
                      {modal.assignment.otherFields.outcome && <p className="col-span-2"><span className="text-gray-500">Kazanım: </span><strong>{modal.assignment.otherFields.outcome}</strong></p>}
                    </div>
                  </div>
                )}
                <div className="mt-4"><OutcomeChips outcomes={modal.assignment.learningOutcomes} /></div>

                {/* Öğrenci teslimi — read-only */}
                <div className="mt-4 bg-white rounded-[12px] p-4 border border-gray-200">
                  <p className="text-[13px] font-bold text-[#3973bf] mb-2">Öğrenci Teslimi</p>
                  {modal.assignment.status === 'completed' && modal.assignment.submissionFileUrl ? (
                    <>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#179e3b] bg-[#59e56e]/15 rounded-full px-3 py-1">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#179e3b" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                          Gönderildi
                        </span>
                        {modal.assignment.late && <span className="inline-flex items-center text-[13px] font-semibold text-[#dc2626] bg-[#fee2e2] rounded-full px-3 py-1">Geç Teslim</span>}
                      </div>
                      {modal.assignment.studentNotes && <p className="text-[13px] text-[#2b2b2b] whitespace-pre-wrap mt-2 mb-2"><span className="text-gray-500">Öğrencinin notu: </span>{modal.assignment.studentNotes}</p>}
                      <a href={modal.assignment.submissionFileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#3973bf] hover:underline mt-1">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3973bf" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                        Öğrencinin yüklediği dosyayı görüntüle
                      </a>
                    </>
                  ) : (
                    <p className="text-[13px] text-gray-500">Öğrenci henüz dosya yüklemedi.</p>
                  )}
                </div>

                {/* Notlandırma */}
                {modal.assignment.graded && (
                  <div className="mt-4 bg-white rounded-[12px] p-4 border border-gray-200">
                    <p className="text-[13px] font-bold text-[#3973bf] mb-2">Notlandırma</p>
                    <div className="flex items-baseline gap-1.5 mb-2">
                      <span className="text-[13px] text-gray-500">Puan:</span>
                      <span className="text-[22px] font-bold text-[#e5599b]">{modal.assignment.gradeScore ?? '-'}</span>
                      <span className="text-[13px] text-gray-400">/ 100</span>
                    </div>
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-[12px] text-gray-500 mb-1">Öğretmen Yorumu</p>
                      {modal.assignment.teacherFeedback ? <p className="text-[13px] text-[#2b2b2b] whitespace-pre-wrap">{modal.assignment.teacherFeedback}</p> : <p className="text-[13px] text-gray-400">Öğretmen yorum eklemedi.</p>}
                    </div>
                  </div>
                )}
              </div>
              <div className="w-full lg:w-[460px] bg-[#fafafa] rounded-[20px] p-6 lg:flex-shrink-0" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                <p className="text-center mb-4" style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#2b2b2b' }}>{modal.assignment.attachmentUrl ? 'Öğretmenin Yüklediği Dosya' : 'Dosya Bulunamadı'}</p>
                {modal.assignment.attachmentUrl ? renderTeacherFile(modal.assignment.attachmentUrl) : (
                  <p className="text-center text-[13px] text-gray-400">Öğretmen bu ödeve dosya eklemedi.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentStudentAssignments;
